import { store } from 'store/store';
import DraftService from 'service/DraftService';

const DRAFT_ID_KEY = 'listingDraftId';

/** Onboarding pages before the property is created on the server (the Amenities step creates it). */
const DRAFT_PATHS = [
  '/host/description',
  '/host/title-and-description',
  '/host/location',
  '/host/amenities',
];

/** The answers worth keeping; the rest of the onboarding state is derived or only exists later. */
export const DRAFT_STATE_KEYS = [
  'place',
  'type',
  'subType',
  'offerings',
  'offeringsMap',
  'detail',
  'location',
  'amenities',
  'amenitiesObject',
] as const;

export const getDraftId = (): string | null => {
  try {
    return localStorage.getItem(DRAFT_ID_KEY);
  } catch (_) {
    return null;
  }
};

export const setDraftId = (id: string | null): void => {
  try {
    if (id) localStorage.setItem(DRAFT_ID_KEY, id);
    else localStorage.removeItem(DRAFT_ID_KEY);
  } catch (_) {
    /* storage unavailable */
  }
};

/** True on a pre-property onboarding page of a brand-new listing (not editing, no property yet). */
export const isDraftPage = (pathname: string, search: string): boolean => {
  if (!DRAFT_PATHS.includes(pathname)) return false;
  if (new URLSearchParams(search).get('id')) return false;
  try {
    if (localStorage.getItem('newPropertId')) return false;
    return !!localStorage.getItem('jwtToken');
  } catch (_) {
    return false;
  }
};

/**
 * Saves what the host has filled in so far as a draft, to continue from the dashboard later.
 * Does nothing until there is something worth keeping. Never throws (a failed save must not
 * disturb the page).
 */
export const saveListingDraft = async (resumePath: string): Promise<void> => {
  try {
    const state: any = store.getState();
    if (!state?.place?.name && !state?.type) return;

    const snapshot: Record<string, unknown> = {};
    DRAFT_STATE_KEYS.forEach((key) => {
      snapshot[key] = state[key];
    });
    const res = await DraftService.save({
      draftId: getDraftId(),
      title: state?.detail?.title || '',
      placeType: state?.place?.name || '',
      resumePath,
      state: JSON.stringify(snapshot),
    });
    setDraftId(res.draftId);
  } catch (_) {
    /* best effort */
  }
};

/** The listing has become a real property (or was abandoned on purpose): drop its draft. */
export const discardListingDraft = async (): Promise<void> => {
  const id = getDraftId();
  setDraftId(null);
  if (!id) return;
  try {
    await DraftService.remove(id);
  } catch (_) {
    /* best effort */
  }
};
